<script setup lang="ts">
import { Ref, onMounted, ref, watch } from 'vue';
import {
  ContainerTypes,
  Helpers,
  initializeNeededServices,
} from '@dandelion/core';
import ColorSelector from './ColorSelector.vue';
import DaysComponent from './DaysComponent.vue';
import { DaysText } from '../enum/DaysText';
import { buildMinorRespiratoryPeriods } from '../utils/MinorRespiratoryUtils';

const props = defineProps({
    context: {
        type: String,
        required: true,
    },
    fullName: {
        type: String,
        required: true,
    },
    id: {
        type: String,
        required: true,
    },
    containerType: {
        type: String,
        required: true,
    },
});

let { currentCaseService, model } = initializeNeededServices(props.context, props.containerType as ContainerTypes);

const PROCEDURES_LIST = 'core.proceduresTreatments.proceduresList';
const MINOR_RESPIRATORY = 'start.minorRespiratory.minorRespiratoryPlugin';
const availablePeriods = ref<Array<any>>([]);

  const days: Ref<{ [key: string]: string }> = ref({});

const minorRespiratoryPluginVar = Helpers.getObjByPath(
  MINOR_RESPIRATORY,
  model
);

onMounted(() => {
  if (!minorRespiratoryPluginVar) {
    Helpers.setObjByPath(MINOR_RESPIRATORY, model, days);
  }

  watch(
    [
      () => Helpers.getObjByPath(PROCEDURES_LIST, model),
      () => model.core?.administrativeInfo?.icuAdmDate,
      () => model.core?.outcome?.icuDisDate,
    ],
    ([]) => {
      if (Object.keys(model).length > 0) {
        buildPeriodsAndRemoveDays();
      }
    },
    { immediate: true, deep: true }
  );
});

function buildPeriodsAndRemoveDays() {
  days.value = Helpers.getObjByPath(MINOR_RESPIRATORY, model);
  availablePeriods.value.splice(
    0,
    availablePeriods.value.length,
    ...buildMinorRespiratoryPeriods(model)
  );

  // Cleaning values for dates that are no longer in available periods
  Object.keys(days.value ?? {}).forEach((day) => {
    const isInSomePeriod = availablePeriods.value.some(
      (period: any) =>
        period.procedureStartDate <= day && period.procedureEndDate >= day
    );
    if (!isInSomePeriod) {
      delete days.value[day];
    }
  });
  Helpers.setObjByPath(MINOR_RESPIRATORY, model, days.value);
}

function setDay(day: string, newValue: string) {
  const model = currentCaseService.getModel();
  days.value = Helpers.getObjByPath(MINOR_RESPIRATORY, model);

  if (newValue === DaysText.DAY_NOT_COMPILED) {
    delete days.value[day];
    Helpers.setObjByPath(MINOR_RESPIRATORY, model, days);
    return;
  }

  days.value[day] = newValue;
  Helpers.setObjByPath(MINOR_RESPIRATORY, model, days);
}

const currentColor = ref(DaysText.GREY);
const colorButtons = [
  {
    color: DaysText.GREY,
    label: DaysText.DAY_NOT_COMPILED,
  },
  {
    color: DaysText.BLUE,
    label: DaysText.DAY_WITHOUT_ASSISTENCE,
  },
  {
    color: DaysText.PURPLE,
    label: DaysText.DAY_WITH_ASSISTENCE,
  },
];

const setColor = (color: DaysText) => {
  currentColor.value = color;
};
</script>

<template>
  <div id="minorRespiratoryContainer">
    <ColorSelector
      :cb-array="colorButtons"
      @setColor="setColor"
    ></ColorSelector>
    <section id="minorRespiratoryPeriodList">
      <template v-for="item in availablePeriods">
        <DaysComponent
          :startDate="item.procedureStartDate"
          :endDate="item.procedureEndDate"
          :color="currentColor"
          :dayValues="days"
          @setDay="setDay"
        />
      </template>
    </section>
  </div>
</template>

<style scoped>
#start\.minorRespiratory\.minorRespiratoryPlugin {
  grid-column: 1 / 3;
  overflow: hidden;
}

#minorRespiratoryContainer,
#minorRespiratoryPeriodList {
  padding: 0;
}
</style>
